import { QueryClient } from '@tanstack/react-query';

// Default stale time: 5 minutes (data is considered fresh for this long)
const STALE_TIME = 1000 * 60 * 5;

// Default cache time (gcTime): 1 hour (inactive query data is kept in cache for this long)
// In TanStack Query v5, cacheTime was renamed to gcTime (garbage collection time).
const GC_TIME = 1000 * 60 * 60;

/**
 * React Query Client Instance.
 *
 * Configured with default options for queries:
 * - `staleTime`: Time until a query is considered stale (5 minutes).
 *   During this time, data is served from cache without a background refetch.
 * - `gcTime`: Time until inactive query data is garbage collected (1 hour).
 *   If a query is not used (no active subscribers) for this duration, its data is removed from the cache.
 * - `retry`: Number of times failed queries will be retried (3 times).
 * - `refetchOnWindowFocus`: Whether to refetch queries when the window regains focus (true).
 *   This helps keep data fresh when the user returns to the application.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      gcTime: GC_TIME, // Use gcTime for TanStack Query v5+
      retry: 3,
      refetchOnWindowFocus: true, // Default is true, explicitly setting for clarity
      // Not setting a default queryFn to allow flexibility, but it's a good pattern
      // for larger apps to enforce providing it or have a very generic erroring one.
    },
    mutations: {
      // Default options for mutations can be set here if needed.
      // For example, it's common to disable retries for mutations by default:
      // retry: 0,
    },
  },
});

// Note: For React Query Devtools integration, this queryClient instance
// will be passed to the <ReactQueryDevtools /> component in your main App setup.
// No further setup for devtools is needed in this file.
